import { MessageResponse } from "@/generated-api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CheckIcon, CopyIcon } from "lucide-react";
import TimeReadStatus from "./TimeReadStatus";
import { useRef, useState, useEffect } from "react";
import { useLongPress } from "use-long-press";
import { isMobile } from "react-device-detect";
import { useTranslation } from "@/hooks/useTranslation";

export default function TextChatBubble({
  counterpartyId,
  message,
}: {
  counterpartyId: string;
  message: MessageResponse;
}) {
  const [isCopied, setIsCopied] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isPressing, setIsPressing] = useState(false);
  const [isSelectable, setIsSelectable] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const selectionTimeout = useRef<NodeJS.Timeout>();
  const t = useTranslation();

  const counterparty = message.sender_id === counterpartyId;

  useEffect(() => {
    return () => {
      if (selectionTimeout.current) {
        clearTimeout(selectionTimeout.current);
      }
    };
  }, []);

  const selectText = () => {
    const element = textRef.current;
    if (element) {
      // Clear any existing selection first
      window.getSelection()?.removeAllRanges();

      // Create and apply the new selection
      const range = document.createRange();
      range.selectNodeContents(element);
      const selection = window.getSelection();
      if (selection) {
        selection.addRange(range);
      }

      // On iOS, we need to ensure the selection sticks
      selectionTimeout.current = setTimeout(() => {
        const currentSelection = window.getSelection();
        if (
          !currentSelection?.rangeCount ||
          currentSelection.toString().length <= 1
        ) {
          selection?.addRange(range);
        }
      }, 100);
    }
  };

  const bind = useLongPress(
    () => {
      setIsPressing(false);
      setIsOpen(true);
      setIsSelectable(true);

      // Give the selectable state time to apply
      setTimeout(() => {
        selectText();
      }, 50);
    },
    {
      onStart: () => {
        setIsPressing(true);
      },
      onFinish: () => {
        setIsPressing(false);
      },
      onCancel: () => {
        setIsPressing(false);
        setIsOpen(false);
        setIsSelectable(false);
        window.getSelection()?.removeAllRanges();
        if (selectionTimeout.current) {
          clearTimeout(selectionTimeout.current);
        }
      },
      threshold: 500,
      cancelOnMovement: true,
      captureEvent: true,
    }
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 1000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  const ChatBubbleContent = () => (
    <div
      className={`${
        counterparty ? "bg-white text-black" : "bg-zinc-800 text-white"
      } px-3 py-2 rounded-3xl inline-block`}
    >
      <div className="relative">
        <p
          ref={textRef}
          className={`text-base text-left break-words whitespace-pre-wrap max-w-xs lg:max-w-md ${
            isSelectable || !isMobile ? "select-text" : "select-none"
          }`}
          style={{
            WebkitUserSelect: isSelectable || !isMobile ? "text" : "none",
            userSelect: isSelectable || !isMobile ? "text" : "none",
          }}
        >
          {message.content}
          <span
            className={`inline-block ${
              counterparty
                ? "w-[2.4rem]"
                : message.read_at
                ? "w-[3.7rem]"
                : "w-[3.4rem]"
            }`}
          ></span>
        </p>
        <TimeReadStatus message={message} counterparty={counterparty} />
      </div>
    </div>
  );

  return (
    <DropdownMenu
      open={isOpen && !isPressing}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          setIsSelectable(false);
          window.getSelection()?.removeAllRanges();
          if (selectionTimeout.current) {
            clearTimeout(selectionTimeout.current);
          }
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        {isMobile ? (
          <div {...bind()}>
            <ChatBubbleContent />
          </div>
        ) : (
          <div
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen(true);
              setIsPressing(false);
            }}
          >
            <ChatBubbleContent />
          </div>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={counterparty ? "start" : "end"}
        className="dark rounded-2xl min-w-0"
      >
        <DropdownMenuItem
          onClick={(e) => {
            e.preventDefault();
            void handleCopy();
          }}
          className="gap-2 py-2 rounded-xl hover:cursor-pointer"
        >
          {isCopied ? <CheckIcon size={16} /> : <CopyIcon size={16} />}
          <span>{t.MESSAGES.LABELS.COPY_BUTTON}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
